import { Box, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { AreaChart, Area } from "recharts";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState, useReducer } from "react";
import "leaflet/dist/leaflet.css";
import { Popup, Polygon, Tooltip } from "react-leaflet";
import L from "leaflet";
import {
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  fetchCities,
  fetchAdministration,
  fetchIndicators,
  fetchLocations,
  testBackendConnection,
} from "../helpers/fetchFunctions";

import {
  handleSum,
  handleAggregation,
  handleAddIndicator,
  handleAddYears,
  handleGenerateVisualization,
  handleUpdateIndicators,
  handleUpdateYear,
  handleDeleteIndicator,
} from "../helpers/eventHandlers";

import {
  getCurrentAdminTypeURL,
  getSelectedAdminInstancesNames,
  getSelectedAdminInstancesURLs,
  mapInstanceURLtoName,
} from "../helpers/reducerHelpers";

import MapView from "./DataVisComponents/MapView";
import IndicatorTable from "./DataVisComponents/Table";
import ActivePie from "./DataVisComponents/ActivePie";
import { Header } from "./SearchPageComponents/Header";

import { Button as JoyButton } from "@mui/joy";
import { Sheet as JoySheet } from "@mui/joy";
import { Box as JoyBox } from "@mui/joy";

import CloseIcon from "@mui/icons-material/Close";
import SaveAsIcon from "@mui/icons-material/SaveAs";

import {
  NewDropdown,
  NewDropdownStateValue,
} from "./SearchPageComponents/NewDropdown";
import { NewDropdownMultiSelect } from "./SearchPageComponents/NewDropdownMultiSelect";
import { NumberInput } from "./SearchPageComponents/NumberInput";
import ComparisonGraph from "./DataVisComponents/ComparisonGraph";

import { adminAreaTypeReducer } from "../reducers/adminAreaTypeReducer";
import { adminAreaInstanceReducer } from "../reducers/adminAreaInstanceReducer";

import DeleteIcon from "@mui/icons-material/Delete";
import LocationSelect from "./OtherComponents/LocationSelect";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const API_BASE_URL = process.env.REACT_APP_API_URL;

//console.log(API_BASE_URL);

/*
 * Implements the search page.
 */
function Dashboard({
  cityURLs,
  setCityURLs,
  adminAreaTypesState,
  dispatchAdminAreaTypes,
  adminAreaInstancesState,
  dispatchAdminAreaInstances,
}) {
  // REFER TO THE DOCUMENTATION PDF FOR MORE DETAILED EXPLANATION OF THE STATES

  /*
   * The time ranges being considered for the indicators.
   */
  const [years, setYears] = useState([{ value1: 0, value2: 0, id: 0 }]);

  /*
   * Indicator names mapped to their unique URIs.
   */
  const [indicatorURLs, setIndicatorURLs] = useState({});

  /*
   * The names of the indicators that are currently selected from each dropdown.
   */
  const [selectedIndicators, setSelectedIndicators] = useState({ 0: "" });

  /*
   * The data for each selected indicator.
   */
  const [indicatorData, setIndicatorData] = useState({});

  /*
   * The polygons used to draw the administrative area instances on the map.
   */
  const [mapPolygons, setMapPolygons] = useState({});

  /*
   * Whether the visualizations should be shown or not.
   * Format: A boolean value, true if the visualizations should show or false if they should not.
   */
  const [showingVisualization, setShowingVisualization] = useState(false);

  /*
   * Whether the visualization generation functions should activate.
   * True if the program is ready for the visualization to generate, false otherwise.
   */
  const [beginGeneration, setBeginGeneration] = useState(false);

  /*
   * The URIs of the currently selected administrative area instances.
   */
  const [currentSelectedMultiIndicators, setCurrentSelectedMultiIndicators] =
    useState([]);

  /*
   * The relevant table column names for each indicator.
   * The first column is always "Admin Area Name", followed by the year range that the user has selected for the current indicator
   */
  const [tableColumns, setTableColumns] = useState({});

  /*
   * The indicators data, formatted for display on the table.
   */
  const [tableData, setTableData] = useState({});

  /*
   * The indicators data, formatted for display on the graphs.
   */
  const [chartData, setChartData] = useState({});

  /*
   * Indicates if an error has occured while trying to generate a visualization.
   * Used for showing an error message if this happens.
   */
  const [showVisError, setShowVisError] = useState(false);

  /*
   * The graph type selected for the FIRST graph of each indicator visualization.
   * Each visualization has two customizable graphs, with various types available: bar, line, etc.
   */
  const [graphTypes, setGraphTypes] = useState({});

  /*
   * The graph type selected for the SECOND graph of each indicator visualization.
   * Each visualization has two customizable graphs, with various types available: bar, line, etc.
   */
  const [comparisonGraphTypes, setComparisonGraphTypes] = useState({});

  /*
   * Indicates if the program is loading the indicator visualization.
   * Used primarily for showing loading indicators while this is going on.
   */
  const [visLoading, setVisLoading] = useState(false);

  /*
   * Indicates if the user has selected a city from the dropdown, which begins loading in values for the other dropdowns.
   * Format: A boolean value, true if loading or false otherwise
   */
  const [cityLoading, setCityLoading] = useState(false);

  // Colour wheel for the visualizations
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ffc0cb",
    "#ff7f50",
    "#ff69b4",
    "#9acd32",
  ];

  // Upon initial page load, fetch list of indicators
  useEffect(() => {
    fetchIndicators(setIndicatorURLs);
    const checkBackend = async () => {
      //console.log("Checking backend connection...");
      const result = await testBackendConnection();
      if (!result) {
        alert(
          "Could not connect to the backend. Please check your connection."
        );
      }
    };
    checkBackend();
  }, []);

  useEffect(() => {
    //console.log("Types State updated:", adminAreaTypesState);
  }, [adminAreaTypesState]);

  // This useEffect is for testing and developement purposes.
  useEffect(() => {
    // //console.log("indicatorURLs:", indicatorURLs);
    // //console.log("Size of indicatorURLs:", Object.keys(indicatorURLs).length);
    // //console.log("selectedIndicators:", selectedIndicators);
    // //console.log("indicatorData:", indicatorData);
    // //console.log("currentSelectedMultiIndicators:", currentSelectedMultiIndicators);
    // //console.log("tabeColumns:", tableColumns);
    // //console.log("tableData:", tableData);
    // //console.log("chartData:", chartData);
    // //console.log("graphTypes:", graphTypes);
    // //console.log("comparisonGraphTypes:", comparisonGraphTypes);
    // //console.log("years:", years);
    // //console.log("END OF USE EFFECT");
  }, [
    cityURLs,
    currentSelectedMultiIndicators,
    indicatorURLs,
    selectedIndicators,
    indicatorData,
    mapPolygons,
    showingVisualization,
    beginGeneration,
    currentSelectedMultiIndicators,
    tableColumns,
    tableData,
    chartData,
    showVisError,
    graphTypes,
    comparisonGraphTypes,
    visLoading,
    cityLoading,
  ]);

  useEffect(() => {
    // Also checks if number of keys in indicatorData is equal to length of selectedIndicators - will indicate if completely done previous step
    if (
      beginGeneration &&
      Object.keys(indicatorData).length ===
        Object.keys(selectedIndicators).length
    ) {
      const currentIndicatorNames = Object.fromEntries(
        Object.entries(indicatorURLs).map(([key, value]) => [value, key])
      );
      const indicatorIndices = Object.fromEntries(
        Object.entries(selectedIndicators).map(([key, value]) => [value, key])
      );

      setMapPolygons({});
      setTableColumns({});
      setTableData({});
      setChartData({});

      Object.keys(indicatorData).forEach((indicator) => {
        var yearRange = [];
        const ind = parseInt(
          indicatorIndices[currentIndicatorNames[indicator]]
        );
        for (let i = years[ind].value1; i <= years[ind].value2; ++i) {
          yearRange.push(i.toString());
        }

        // Set table information: column names and data
        setTableColumns((prevColumns) => ({
          ...prevColumns,
          [indicator]: ["Admin Area Name"].concat(yearRange),
        }));

        setTableData((prevData) => ({
          ...prevData,
          [indicator]: Object.entries(indicatorData[indicator].data).map(
            ([instanceURL, data]) =>
              [
                mapInstanceURLtoName(adminAreaInstancesState, instanceURL),
              ].concat(Object.entries(data).map(([year, value]) => value))
          ),
        }));

        // Set chart information
        const tempChartData = yearRange.map((year) => {
          const res = { name: year };

          const selectedAdminInstancesURLs = getSelectedAdminInstancesURLs(
            adminAreaInstancesState
          );

          // we want to get an object of type { "Area Name": value, "Area Name": value, ... }

          const val = Object.fromEntries(
            selectedAdminInstancesURLs.map((instanceURL) => [
              mapInstanceURLtoName(adminAreaInstancesState, instanceURL),
              indicatorData[indicator].data[instanceURL][year],
            ])
          );

          return { ...res, ...val };
        });

        setChartData((oldData) => ({
          ...oldData,
          [indicator]: tempChartData,
        }));

        // Set map information

        const itemColor = (key) => {
          if (Object.keys(indicatorData[indicator].data).indexOf(key) !== -1) {
            return "green";
          } else {
            return "red";
          }
        };

        const newPolygons = Object.keys(adminAreaInstancesState).map((key) => {
          const instanceURL = adminAreaInstancesState[key].URL;
          return (
            <Polygon
              key={instanceURL}
              pathOptions={{ color: itemColor(instanceURL) }}
              positions={adminAreaInstancesState[key].coordinates}
            >
              {Object.keys(indicatorData[indicator].data).indexOf(
                instanceURL
              ) === -1 ? (
                <>
                  <Tooltip sticky>
                    <strong>
                      {mapInstanceURLtoName(
                        adminAreaInstancesState,
                        instanceURL
                      )}
                    </strong>{" "}
                    <br />
                    Area was not selected
                  </Tooltip>
                  <Popup>
                    <strong>
                      {mapInstanceURLtoName(
                        adminAreaInstancesState,
                        instanceURL
                      )}
                    </strong>{" "}
                    <br />
                    Area was not selected
                  </Popup>
                </>
              ) : (
                <>
                  <Tooltip sticky>
                    <strong>
                      {mapInstanceURLtoName(
                        adminAreaInstancesState,
                        instanceURL
                      )}
                    </strong>{" "}
                    <br />
                    {selectedIndicators[ind]}:<br />
                    {Object.entries(
                      indicatorData[indicator].data[instanceURL]
                    ).map(([year, value]) => (
                      <div
                        key={mapInstanceURLtoName(
                          adminAreaInstancesState,
                          instanceURL
                        )}
                      >
                        {value} ({year})
                      </div>
                    ))}
                  </Tooltip>
                  <Popup>
                    <strong>
                      {mapInstanceURLtoName(
                        adminAreaInstancesState,
                        instanceURL
                      )}
                    </strong>{" "}
                    <br />
                    {selectedIndicators[ind]}:<br />
                    {Object.entries(
                      indicatorData[indicator].data[instanceURL]
                    ).map(([year, value]) => (
                      <div
                        key={mapInstanceURLtoName(
                          adminAreaInstancesState,
                          instanceURL
                        )}
                      >
                        {value} ({year})
                      </div>
                    ))}
                  </Popup>
                </>
              )}
            </Polygon>
          );
        });
        // Change
        // setMapPolygons((oldPolygons) => ({
        //   ...oldPolygons,
        //   [indicator]: { polygons: newPolygons, index: ind },
        // }));
        const indicatorTitle = selectedIndicators[ind];
        setMapPolygons((oldPolygons) => ({
          ...oldPolygons,
          [indicator]: {
            polygons: newPolygons,
            title: indicatorTitle, // store the actual selected indicator name
          },
        }));
      });

      if (!showingVisualization) {
        setShowingVisualization(true);
      }
      setBeginGeneration(false);
    }
  }, [
    beginGeneration,
    indicatorData,
    indicatorURLs,
    adminAreaInstancesState,
    mapPolygons,
    selectedIndicators,
    showingVisualization,
    years,
  ]);

  return (
    <Container
      maxWidth="lg"
      sx={{ marginTop: { xs: "100px", md: "30px" }, paddingBottom: "100px" }}
    >
      {/* Input Form */}
      <Stack spacing={3}>
        <Header pageName={"Search Indicators"} />

        {/* Location and Admin Area Selection */}
        <LocationSelect
          cityURLs={cityURLs}
          setCityURLs={setCityURLs}
          adminAreaTypesState={adminAreaTypesState}
          dispatchAdminAreaTypes={dispatchAdminAreaTypes}
          adminAreaInstancesState={adminAreaInstancesState}
          dispatchAdminAreaInstances={dispatchAdminAreaInstances}
          isGeneratingVisualization={visLoading}
        />
        <JoyBox>
          <JoyBox
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            {/* Section Title */}
            <Typography
              variant="h5"
              style={{
                fontFamily: "Trade Gothic Next LT Pro Cn, sans-serif",
                fontSize: 35,
                fontWeight: "bold",
                color: "#0b2f4e",
              }}
            >
              Indicator Information
            </Typography>
          </JoyBox>
          {/* User Input Form Section */}
          <JoySheet
            variant="outlined"
            sx={{ p: 2, borderRadius: "sm", paddingBottom: "50px" }}
          >
            <Grid container>
              <Grid xs="12" md="6">
                <JoyBox
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "40px",
                  }}
                >
                  <Stack spacing={5}>
                    {Object.entries(selectedIndicators).map(
                      ([index, value]) => (
                        <div>
                          <NewDropdown
                            key={`indicator-${index}`}
                            id="indicator-input"
                            // disabled={getCurrentAdminTypeURL(adminAreaTypesState) === null}
                            disabled={
                              visLoading ||
                              getCurrentAdminTypeURL(adminAreaTypesState) ===
                                null
                            }
                            label={`Indicator #${parseInt(index) + 1}`}
                            value={value}
                            options={Object.keys(indicatorURLs)}
                            onChange={(event, newValue) =>
                              handleUpdateIndicators(
                                parseInt(index),
                                newValue,
                                setSelectedIndicators
                              )
                            }
                            desc=""
                          />
                        </div>
                      )
                    )}
                    <JoyBox>
                      <JoyButton
                        sx={{ width: "25%" }}
                        variant="soft"
                        color="danger"
                        onClick={(event) =>
                          handleDeleteIndicator(
                            years,
                            selectedIndicators,
                            setYears,
                            setSelectedIndicators,
                            setCurrentSelectedMultiIndicators
                          )
                        }
                      >
                        <DeleteIcon />
                      </JoyButton>
                      <JoyButton
                        sx={{ width: "73%", marginLeft: "2%" }}
                        variant="soft"
                        onClick={() => {
                          handleAddIndicator(
                            selectedIndicators,
                            setSelectedIndicators
                          );
                          handleAddYears(years, setYears);
                        }}
                      >
                        <AddIcon />
                      </JoyButton>
                    </JoyBox>
                  </Stack>
                </JoyBox>
              </Grid>
              <Grid xs="12" md="6">
                <Stack spacing={5} sx={{ marginTop: "40px" }}>
                  {years.map(({ id, start, end }) => (
                    <JoyBox
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "40px",
                      }}
                    >
                      <NumberInput
                        id={`year1-${id}`}
                        disabled={
                          getCurrentAdminTypeURL(adminAreaTypesState) === null
                        }
                        label={`Starting Year ${id + 1}`}
                        onChange={(event) =>
                          handleUpdateYear(id, "start", event, years, setYears)
                        }
                        value={start}
                        desc=""
                      />
                      <NumberInput
                        id={`year2-${id}`}
                        disabled={
                          getCurrentAdminTypeURL(adminAreaTypesState) === null
                        }
                        label={`Ending Year ${id + 1}`}
                        onChange={(event) =>
                          handleUpdateYear(id, "end", event, years, setYears)
                        }
                        value={end}
                        desc=""
                      />
                    </JoyBox>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </JoySheet>
        </JoyBox>
        <JoyBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "40px",
          }}
        >
          {/* Generate Visualization Button */}
          <JoyButton
            disabled={getCurrentAdminTypeURL(adminAreaTypesState) === null}
            size="lg"
            color="success"
            endDecorator={<>{">"}</>}
            onClick={() =>
              handleGenerateVisualization(
                years,
                cityURLs,
                adminAreaTypesState,
                indicatorURLs,
                selectedIndicators,
                adminAreaInstancesState,
                showVisError,
                setMapPolygons,
                setShowVisError,
                setIndicatorData,
                setBeginGeneration,
                setShowingVisualization,
                setVisLoading
              )
            }
            loading={visLoading}
            loadingPosition="start"
          >
            Generate Visualization
          </JoyButton>
        </JoyBox>
      </Stack>

      {/* If there's anything incorrect, display an error */}
      {showVisError && (
        <Typography variant="h6" align="center" sx={{ color: "red" }}>
          ERROR: Could not generate visualization due to missing/improper data.
          <br />
          Please check form inputs.
        </Typography>
      )}

      {/* Show visualization only if set to show */}
      {showingVisualization && (
        <Stack spacing={3} sx={{ marginTop: 5 }}>
          <JoyBox
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: "40px",
            }}
          >
            <JoyButton
              size="sm"
              variant="soft"
              color="danger"
              endDecorator={<CloseIcon />}
              onClick={() => setShowingVisualization(false)}
            >
              Close Visualization
            </JoyButton>
          </JoyBox>
          {/* <Button variant="outlined" size="small" sx={{width:'200px'}} onClick={() => setShowingVisualization(false)}>Close Visualization</Button> */}

          {/* Visualizations, one for each indicator */}
          {Object.keys(mapPolygons).map((indicator) => (
            <Paper sx={{ padding: "20px", paddingBottom: "50px" }}>
              <Stack spacing={3}>
                {/* Header - Indicator name */}
                <Typography
                  variant="h4"
                  align="center"
                  style={{
                    fontFamily: "Trade Gothic Next LT Pro Cn, sans-serif",
                    fontSize: 35,
                    fontWeight: "bold",
                  }}
                  sx={{}}
                >
                  {mapPolygons[indicator].title}
                </Typography>
                <JoyBox
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* Header - Save visualization button */}
                  <JoyButton
                    size="sm"
                    sx={{ width: "50%" }}
                    variant="solid"
                    color="primary"
                    endDecorator={<SaveAsIcon />}
                    onClick={() => setShowingVisualization(false)}
                  >
                    Save Visualization
                  </JoyButton>
                </JoyBox>

                {/* Tabular visualization of desired indicator data */}
                <IndicatorTable
                  // defaultTheme={defaultTheme}
                  selectedIndicators={selectedIndicators}
                  mapPolygons={mapPolygons}
                  indicator={indicator}
                  tableColumns={tableColumns}
                  tableData={tableData}
                />

                <Grid container>
                  <Grid sm="12" md="6">
                    <JoyBox sx={{ minHeight: "100px", alignItems: "center" }}>
                      {/* Map visualization of indicator data in desired areas*/}
                      <MapView
                        mapPolygons={mapPolygons}
                        indicator={indicator}
                      />
                    </JoyBox>
                  </Grid>

                  {/* Graph Section */}
                  <Grid sm="12" md="6">
                    {/* Dropdowns for Graph Types */}
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        textAlign: "center",
                        alignItems: "center",
                      }}
                    >
                      <Grid
                        sm="6"
                        md="12"
                        lg="6"
                        sx={{
                          maxWidth: "140px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <NewDropdownStateValue
                          id={`change-graph-${indicator}-1`}
                          label="Graph Type 1"
                          options={["Bar", "Line"]}
                          disabled={false}
                          onChange={(event, newValue) => {
                            setGraphTypes((prevGraphTypes) => ({
                              ...prevGraphTypes,
                              [indicator]: newValue,
                            }));
                          }}
                          value={graphTypes[indicator] || "Bar"} // Default
                          desc=""
                        />
                      </Grid>
                      <Grid
                        sm="6"
                        md="12"
                        lg="6"
                        sx={{
                          maxWidth: "140px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <NewDropdownStateValue
                          id={`change-graph-${indicator}-2`}
                          label="Graph Type 2"
                          options={["Area", "Pie"]}
                          disabled={false}
                          onChange={(event, newValue) => {
                            setComparisonGraphTypes(
                              (prevComparisonGraphTypes) => ({
                                ...prevComparisonGraphTypes,
                                [indicator]: newValue,
                              })
                            );
                          }}
                          value={comparisonGraphTypes[indicator] || "Area"} // Default
                          desc=""
                        />
                      </Grid>
                    </Grid>

                    <ResponsiveContainer width="100%" height={300}>
                      {/* Conditional rendering based on selected graph type */}
                      {graphTypes[indicator] === "Line" ? (
                        // Render LineChart based on graphTypes[indicator]
                        <LineChart
                          data={chartData[indicator]}
                          margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                        >
                          {getSelectedAdminInstancesNames(
                            adminAreaInstancesState
                          ).map((instanceName, index) => (
                            <Line
                              key={instanceName} // Add a unique key for each Line
                              type="monotone"
                              dataKey={instanceName}
                              stroke={colors[index % colors.length]} // Use colors[index] to assign a color
                            />
                          ))}
                          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <ChartTooltip />
                        </LineChart>
                      ) : (
                        <JoyBox
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            minHeight: "550px",
                          }}
                        >
                          {/* <ActivePie data={chartData[indicator]}></ActivePie> */}
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              width={730}
                              height={250}
                              data={chartData[indicator]}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <ChartTooltip />
                              <Legend />

                              {getSelectedAdminInstancesNames(
                                adminAreaInstancesState
                              ).map((instanceName, index) => (
                                <Bar
                                  dataKey={instanceName}
                                  fill={colors[index % colors.length]}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </JoyBox>
                      )}
                    </ResponsiveContainer>

                    {/* Graph 3 is a pie chart */}
                    {/* Pie or Area Chart for Comparison */}
                    <JoyBox sx={{ display: "flex", justifyContent: "center" }}>
                      {comparisonGraphTypes[indicator] === "Pie" ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            overflow: "visible", // Ensure labels outside the chart are not clipped
                            width: "100%", // Adjust the width to ensure enough space
                            height: "auto", // Allow auto height for responsive charts
                          }}
                        >
                          <ActivePie
                            data={handleAggregation(indicator, chartData)}
                          ></ActivePie>
                        </Box>
                      ) : (
                        <ResponsiveContainer width="100%" height={300}>
                          <AreaChart
                            width={500}
                            height={400}
                            data={handleSum(indicator, chartData)}
                            margin={{
                              top: 10,
                              right: 30,
                              left: 0,
                              bottom: 0,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <ChartTooltip />
                            {getSelectedAdminInstancesNames(
                              adminAreaInstancesState
                            ).map((instanceName, index) => (
                              <Area
                                type="monotone"
                                dataKey={instanceName}
                                fill={colors[index % colors.length]}
                                stackId={1}
                              />
                            ))}
                            <Area
                              type="monotone"
                              dataKey="total"
                              fill="#000000"
                              stackId={1}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </JoyBox>
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          ))}
          {/* Multi-Indicator Comparison Graph */}
          <Grid container spacing={2} sx={{ paddingTop: "150px" }}>
            <Grid item xs={12} md={12} lg={3}>
              <JoyBox
                sx={{
                  paddingTop: "15%",
                  display: "flex",
                  justifyContent: "center",
                }}
                key={selectedIndicators}
              >
                <NewDropdownMultiSelect
                  id="multiple-indicator-select"
                  label="Select Indicators"
                  options={Object.values(selectedIndicators)}
                  onChange={(event, newValue) => {
                    setCurrentSelectedMultiIndicators(
                      String(newValue).split(",")
                      // On autofill we get a stringified value.
                      // typeof value === 'string' ? value.split(',') : value,
                    );
                    setCurrentSelectedMultiIndicators(
                      String(newValue).split(",")
                    );
                  }}
                  desc="Select the indicators you wish to compare."
                  currentlySelected={currentSelectedMultiIndicators}
                />
              </JoyBox>
            </Grid>
            <Grid item xs={12} md={12} lg={9}>
              <JoyBox>
                <ComparisonGraph
                  data={indicatorData}
                  indicators={currentSelectedMultiIndicators}
                  colors={colors}
                />
              </JoyBox>
            </Grid>
          </Grid>
        </Stack>
      )}
    </Container>
  );
}

export default Dashboard;
